import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { captureException } from '../../../services/sentryService';
import { ContainerType, CustomerType } from '../../../types';
import HtmlEmbeddedAlert from '../../Banners/HtmlEmbeddedAlert';
import { useCreditcardService } from '../../CreditCardsMutationProvider';
import { useErrorContext } from '../../ErrorContextProvider';
import StoredCreditCards from '../../StoredCreditCards/StoredCreditCards';

import BraintreeCreditCardForm, {
  BraintreeCreditCardFormHandle,
  TokenizeCardResult,
  TokenizeCardResultData,
} from './BraintreeCreditCardForm';

export type BraintreePaymentMethodProps = {
  containerType: ContainerType;
  token: string;
  isFallbackPsp: boolean;
  customerType: CustomerType;
};

type TokenizeResult = {
  redirect: boolean;
  data?: TokenizeCardResultData;
};

const BraintreePaymentMethod: React.FC<BraintreePaymentMethodProps> = ({
  containerType,
  customerType,
  token,
  isFallbackPsp,
}) => {
  const { t } = useTranslation();
  const formHandle = useRef<BraintreeCreditCardFormHandle>(null);
  const { error, setError } = useErrorContext();
  const {
    data: { creditCards },
  } = useCreditcardService();

  // Callback for shop-checkout
  const braintreeTokenizeCard = useCallback(async (): Promise<TokenizeResult> => {
    if (error && !error?.retryable) {
      throw new Error(error.message);
    } else if (!formHandle.current) {
      return { redirect: true };
    } else {
      let tokenizationResult: TokenizeCardResult;
      try {
        tokenizationResult = await formHandle.current?.tokenizeCard();
      } catch (error: any) {
        setError({
          message: t('checkout.payment.creditcard.error.tokenization.retry'),
          retryable: false,
        });
        throw captureException({ message: 'braintree_tokenization_failed' });
      }
      if (tokenizationResult.success) {
        return { redirect: false, data: tokenizationResult.data };
      } else {
        if (tokenizationResult.errorDisplayable) {
          setError({
            message: t('checkout.payment.creditcard.error.tokenization.retry'),
            retryable: true,
          });
        }
        throw new Error('braintree_tokenization_failed');
      }
    }
  }, [t, error, setError]);

  // For calling from parent page
  useEffect(() => {
    (window as any).Payment = {
      ...((window as any).Payment || {}),
      braintreeTokenizeCard,
    };
    return () => {
      (window as any).Payment.braintreeTokenizeCard = undefined;
    };
  }, [braintreeTokenizeCard]);

  const displayPossible = !error || error?.retryable;
  const isMyAccount = containerType === 'myaccount';
  const isGuest = customerType === 'GUEST_CUSTOMER';

  /*
    Just temporary measure until https://tracker.zooplus.de/browse/FIN-5994 is resolved:
    don't render the warning message on the payment-frontend because it is already rendered on the shop-checkout

      // Render warning message in case of emergency
      useEffect(() => {
        if (isFallbackPsp) {
          const errorMessage = isMyAccount
            ? t('myaccount.credit.card.payment.psp.failure.message')
            : t('checkout.credit.card.payment.psp.failure.message');

          setError({
            message: errorMessage,
            retryable: true,
          });
        }
      }, [t, isFallbackPsp, setError, isMyAccount]);
  */

  return (
    <>
      {displayPossible && (!!creditCards?.length || isMyAccount) && (
        <StoredCreditCards isMyAccount={isMyAccount} isFallbackPsp={isFallbackPsp} />
      )}
      {displayPossible && !creditCards?.length && !isMyAccount && (
        <>
          <HtmlEmbeddedAlert innerHtml={t('checkout.payment.creditcard.3d.secure.message')} />
          <BraintreeCreditCardForm
            token={token}
            ref={formHandle}
            containerType={containerType}
            labelsInline={containerType !== 'registration'}
            allowVaulting={!isFallbackPsp && !isGuest}
          />
        </>
      )}
    </>
  );
};

export default BraintreePaymentMethod;
